import React from 'react';
import { Pressable, Text, StyleSheet, ViewStyle, TextStyle, ActivityIndicator } from 'react-native';
import { useTheme } from '../../contexts/ThemeContext';
import { createStyles } from '../../shared/utils/styles';
import { DesignTokens } from '../../shared/types/tokens';
import { Ionicons } from '@expo/vector-icons';

export interface TextButtonProps {
  title: string;
  onPress: () => void;
  variant?: 'primary' | 'secondary' | 'danger' | 'default';
  size?: 'sm' | 'md' | 'lg';
  disabled?: boolean;
  loading?: boolean;
  leftIcon?: keyof typeof Ionicons.glyphMap;
  rightIcon?: keyof typeof Ionicons.glyphMap;
  style?: ViewStyle;
  textStyle?: TextStyle;
}

export function TextButton({
  title,
  onPress,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  leftIcon,
  rightIcon,
  style,
  textStyle,
}: TextButtonProps) {
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const styles = makeStyles(tokens, utils);

  const variantTextStyle = styles[`${variant}Text` as keyof typeof styles] as TextStyle;
  const sizeKey = `size${size.charAt(0).toUpperCase() + size.slice(1)}Text` as 'sizeSmText' | 'sizeMdText' | 'sizeLgText';
  const sizeTextStyle = styles[sizeKey] as TextStyle;
  const disabledStyle = (disabled || loading) ? styles.disabled : null;

  const iconColor = variant === 'primary'
    ? tokens.color.primary
    : variant === 'secondary'
      ? tokens.color.secondary
      : variant === 'danger'
        ? tokens.color.error
        : tokens.color.onSurface;

  const iconSize = size === 'sm' ? 14 : size === 'md' ? 16 : 18;

  return (
    <Pressable
      onPress={onPress}
      disabled={disabled || loading}
      style={({ pressed }) => [
        styles.base,
        disabledStyle,
        pressed && !disabled && !loading && styles.pressed,
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator 
          size="small" 
          color={iconColor} 
        />
      ) : (
        <React.Fragment>
          {leftIcon && (
            <Ionicons 
              name={leftIcon} 
              size={iconSize} 
              color={iconColor} 
              style={styles.leftIcon}
            />
          )}
          <Text style={[variantTextStyle, sizeTextStyle, textStyle]}>
            {title}
          </Text>
          {rightIcon && (
            <Ionicons 
              name={rightIcon} 
              size={iconSize} 
              color={iconColor} 
              style={styles.rightIcon}
            />
          )}
        </React.Fragment>
      )}
    </Pressable>
  );
}

function makeStyles(tokens: DesignTokens, utils: ReturnType<typeof createStyles>) {
  return StyleSheet.create({
    base: {
      paddingHorizontal: utils.spacing.sm,
      paddingVertical: utils.spacing.xs,
      alignItems: 'center',
      justifyContent: 'center',
      flexDirection: 'row',
    },
    leftIcon: {
      marginRight: utils.spacing.xs,
    },
    rightIcon: {
      marginLeft: utils.spacing.xs,
    },
    // Sizes
    sizeSmText: {
      fontSize: utils.typography.sizes.sm,
    },
    sizeMdText: {
      fontSize: utils.typography.sizes.base,
    },
    sizeLgText: {
      fontSize: utils.typography.sizes.lg,
    },
    // Variants
    primaryText: {
      color: tokens.color.primary,
      fontWeight: utils.typography.weights.medium,
      fontFamily: utils.typography.body.fontFamily,
    },
    secondaryText: {
      color: tokens.color.secondary,
      fontWeight: utils.typography.weights.medium,
      fontFamily: utils.typography.body.fontFamily,
    },
    dangerText: {
      color: tokens.color.error,
      fontWeight: utils.typography.weights.medium,
      fontFamily: utils.typography.body.fontFamily,
    },
    defaultText: {
      color: tokens.color.onSurface,
      fontWeight: utils.typography.weights.medium,
      fontFamily: utils.typography.body.fontFamily,
    },
    disabled: {
      opacity: 0.6,
    },
    pressed: {
      opacity: 0.8,
    },
  });
}

